/**
 * Standalone page opened in an external browser (Chrome Custom Tab) by the Flutter app.
 * Reads Razorpay order/subscription params from the URL query string, auto-opens the
 * Razorpay modal with redirect:true, then redirects to the callback_url deep link so
 * Flutter can catch it and hand the payment fields back to the WebView.
 *
 * URL params:
 *   key_id, amount, currency, description
 *   order_id         — one-time order checkout
 *   subscription_id  — autopay subscription checkout
 *   autopay          — 'true' | 'false'
 *   prefill_name, prefill_email, prefill_contact
 *   callback_url     — deep link Flutter listens for (e.g. glammify://razorpay/callback)
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';

const SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';
const PURPLE = '#6318C6';

function loadCheckoutScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) { resolve(); return; }
    const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Failed to load Razorpay script'));
    document.body.appendChild(s);
  });
}

export default function RazorpayRedirectPage() {
  const params = new URLSearchParams(window.location.search);

  const key_id        = params.get('key_id') || '';
  const order_id      = params.get('order_id') || params.get('rzp_order_id') || '';
  const subscription_id = params.get('subscription_id') || params.get('razorpay_subscription_id') || '';
  const autopay       = params.get('autopay') === 'true';
  const amount        = parseInt(params.get('amount') || '0', 10);
  const currency      = params.get('currency') || 'INR';
  const description   = params.get('description') || 'Subscription payment';
  const prefill_name  = params.get('prefill_name') || '';
  const prefill_email = params.get('prefill_email') || '';
  const prefill_contact = params.get('prefill_contact') || '';
  const callback_url  = params.get('callback_url') || '';

  const [status, setStatus]   = useState('loading'); // loading | open | dismissed | error
  const [errorMsg, setErrorMsg] = useState('');
  const rzpRef = useRef(null);

  const openModal = async () => {
    setStatus('loading');
    try {
      await loadCheckoutScript();

      const opts = {
        key:         key_id,
        amount,
        currency,
        name:        'Glammify',
        description,
        image:       '/assets/img/glammify-logo.png',
        prefill: {
          name:    prefill_name,
          email:   prefill_email,
          contact: prefill_contact,
        },
        theme: { color: PURPLE },
        handler: (response) => {
          // Build deep link with payment params; Chrome Custom Tab intercepts it → Flutter catches it
          const p = new URLSearchParams();
          if (response.razorpay_payment_id) p.set('razorpay_payment_id', response.razorpay_payment_id);
          if (response.razorpay_order_id)   p.set('razorpay_order_id',   response.razorpay_order_id);
          if (response.razorpay_subscription_id) p.set('razorpay_subscription_id', response.razorpay_subscription_id);
          if (response.razorpay_signature)  p.set('razorpay_signature',  response.razorpay_signature);
          window.location.href = `${callback_url}?${p.toString()}`;
        },
        modal: {
          ondismiss: () => setStatus('dismissed'),
        },
      };

      if (autopay && subscription_id) {
        opts.subscription_id = subscription_id;
      } else {
        opts.order_id = order_id;
      }

      rzpRef.current = new window.Razorpay(opts);
      rzpRef.current.open();
      setStatus('open');
    } catch (e) {
      setErrorMsg(e.message || 'Could not load payment gateway.');
      setStatus('error');
    }
  };

  useEffect(() => {
    if (!key_id || (!order_id && !subscription_id) || !callback_url) {
      setErrorMsg('Invalid payment link. Please go back and try again.');
      setStatus('error');
      return;
    }
    openModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currSymbol = currency === 'USD' ? '$' : '₹';
  const displayAmount = (amount / 100).toLocaleString(
    currency === 'USD' ? 'en-US' : 'en-IN',
    { minimumFractionDigits: 2 },
  );

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      bgcolor: '#f9fafb', px: 2,
    }}>
      <Box sx={{
        bgcolor: '#fff', borderRadius: 3, p: 4, maxWidth: 360, width: '100%',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center',
      }}>
        <Box component="img"
          src="/assets/img/glammify-logo.png"
          alt="Glammify"
          sx={{ height: 40, mb: 2, objectFit: 'contain' }}
          onError={e => { e.target.style.display = 'none'; }}
        />

        <Typography fontWeight={700} fontSize={18} mb={0.5}>
          {description}
        </Typography>
        <Typography fontSize={22} fontWeight={800} sx={{ color: PURPLE }} mb={2.5}>
          {currSymbol} {displayAmount}
        </Typography>

        {status === 'loading' && (
          <>
            <CircularProgress size={32} sx={{ color: PURPLE, mb: 1.5 }} />
            <Typography fontSize={13} color="text.secondary">
              Opening payment gateway…
            </Typography>
          </>
        )}

        {status === 'open' && (
          <Typography fontSize={13} color="text.secondary">
            Complete your payment in the Razorpay window.
          </Typography>
        )}

        {status === 'dismissed' && (
          <>
            <Typography fontSize={13} color="text.secondary" mb={2}>
              Payment window closed. Tap below to try again.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={openModal}
              sx={{
                bgcolor: PURPLE, borderRadius: '10px', fontWeight: 700,
                textTransform: 'none', fontSize: 15, py: 1.25,
                '&:hover': { bgcolor: '#5210a8' },
              }}
            >
              Retry Payment
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Typography fontSize={13} color="error" mb={2}>
              {errorMsg}
            </Typography>
            {key_id && (order_id || subscription_id) && callback_url && (
              <Button
                variant="outlined"
                fullWidth
                onClick={openModal}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
              >
                Try Again
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
